/**
 * Guide des nouveautés — item dans Outils & Liens Utiles.
 *
 * Regroupe toutes les bannières de nouveautés du CRM (adaptées au rôle),
 * y compris celles déjà vues, pour que l'utilisateur puisse relire à tout
 * moment ce qui a changé sans encombrer le dashboard. Chaque item est
 * repliable/dépliable. Un bouton "Revoir sur mon dashboard" permet de
 * remettre l'annonce dans la file d'attente du dashboard.
 *
 * Placé volontairement en bas de la page — la "Nouvelle vision Valnexo"
 * et les "Documents utiles à vos prospections" restent les 2 sections
 * les plus visibles et importantes.
 */

import { useEffect, useMemo, useState } from 'react';
import { announcementsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
    BookmarkCheck, ChevronDown, ChevronUp, Sparkles, CheckCircle2, RotateCcw, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

function AnnouncementRow({ item, onReset, defaultOpen }) {
    const [open, setOpen] = useState(!!defaultOpen);
    const seen = !!item.dismissed;

    return (
        <div
            className={cn(
                'rounded-xl border transition-all',
                seen ? 'border-slate-200 bg-white' : 'border-purple-200 bg-purple-50/40',
                open && 'shadow-sm'
            )}
            data-testid={`announcement-catalog-item-${item.id}`}
        >
            <button
                type="button"
                onClick={() => setOpen(v => !v)}
                className="w-full flex items-start gap-3 p-4 text-left hover:bg-slate-50/60 rounded-xl"
                data-testid={`announcement-catalog-toggle-${item.id}`}
            >
                <div className={cn(
                    'w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
                    seen ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'
                )}>
                    {seen ? <CheckCircle2 className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={cn(
                            'text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5',
                            seen ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                 : 'bg-purple-100 text-purple-700 border border-purple-200'
                        )}>
                            {seen ? 'Déjà vue' : 'À découvrir'}
                        </span>
                    </div>
                    <p className="text-sm font-semibold text-slate-800 mt-1">{item.title}</p>
                    {!open && item.intro && (
                        <p className="text-xs text-slate-500 mt-1 line-clamp-1">{item.intro}</p>
                    )}
                </div>
                <div className="shrink-0 pt-1 text-slate-400">
                    {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
            </button>
            {open && (
                <div className="border-t border-slate-100 p-4 space-y-3">
                    {item.intro && (
                        <p className="text-sm text-slate-600 leading-relaxed">{item.intro}</p>
                    )}
                    <ul className="space-y-2">
                        {(item.points || []).map((p, i) => (
                            <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                                <CheckCircle2 className="h-4 w-4 text-purple-500 mt-0.5 shrink-0" />
                                <span>{p}</span>
                            </li>
                        ))}
                    </ul>
                    {seen && (
                        <div className="flex justify-end pt-1">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onReset(item)}
                                data-testid={`announcement-catalog-reset-${item.id}`}
                                className="text-purple-700 border-purple-200 hover:bg-purple-50"
                            >
                                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                                Remettre sur mon dashboard
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function AnnouncementsCatalog() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [filter, setFilter] = useState('all'); // 'all' | 'todo' | 'seen'

    const load = async () => {
        setLoading(true);
        try {
            const res = await announcementsAPI.all();
            setItems(res.data?.announcements || []);
        } catch { /* noop */ }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        if (filter === 'todo') return items.filter(i => !i.dismissed);
        if (filter === 'seen') return items.filter(i => i.dismissed);
        return items;
    }, [items, filter]);

    const todoCount = items.filter(i => !i.dismissed).length;

    const handleReset = async (item) => {
        try {
            await announcementsAPI.reset(item.id);
            toast.success(`« ${item.title} » réapparaîtra sur votre dashboard`);
            setItems(prev => prev.map(x => x.id === item.id ? { ...x, dismissed: false } : x));
        } catch (e) {
            toast.error('Impossible de réafficher cette nouveauté');
        }
    };

    if (loading) return null;
    if (items.length === 0) return null;

    return (
        <Card
            className="border-purple-200 bg-gradient-to-br from-purple-50/40 via-white to-fuchsia-50/30"
            data-testid="announcements-catalog-card"
        >
            <CardHeader
                onClick={() => setExpanded(v => !v)}
                className="cursor-pointer pb-3 hover:bg-purple-50/40 transition-colors"
                data-testid="announcements-catalog-header"
            >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center shadow-sm shrink-0">
                            <BookmarkCheck className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl font-bold text-slate-900">
                                Guide des nouveautés du CRM
                                {todoCount > 0 && (
                                    <span
                                        data-testid="announcements-catalog-badge-todo"
                                        className="text-[10px] font-bold text-white bg-purple-600 rounded-full px-2 py-0.5"
                                    >
                                        {todoCount} à voir
                                    </span>
                                )}
                            </CardTitle>
                            <p className="text-xs text-slate-500 mt-1">
                                Toutes les évolutions du CRM, expliquées — même celles que tu as déjà validées
                            </p>
                        </div>
                    </div>
                    <Button
                        size="sm"
                        variant="outline"
                        className="border-purple-300 text-purple-700 hover:bg-purple-50"
                        data-testid="announcements-catalog-expand"
                    >
                        {expanded ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                        {expanded ? 'Replier' : `Voir les ${items.length} nouveautés`}
                    </Button>
                </div>
            </CardHeader>

            {expanded && (
                <CardContent className="pt-0 space-y-3" data-testid="announcements-catalog-body">
                    {/* Filtres compacts */}
                    <div className="flex items-center gap-2 flex-wrap pb-2 border-b border-slate-100">
                        {[
                            { k: 'all', label: `Toutes (${items.length})` },
                            { k: 'todo', label: `À découvrir (${todoCount})` },
                            { k: 'seen', label: `Déjà vues (${items.length - todoCount})` },
                        ].map(f => (
                            <Button
                                key={f.k}
                                size="sm"
                                variant={filter === f.k ? 'default' : 'outline'}
                                onClick={() => setFilter(f.k)}
                                data-testid={`announcements-catalog-filter-${f.k}`}
                                className={cn(
                                    'h-7 text-xs',
                                    filter === f.k
                                        ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                        : 'border-purple-200 text-purple-700 hover:bg-purple-50'
                                )}
                            >
                                {f.label}
                            </Button>
                        ))}
                        <p className="ml-auto text-[11px] text-slate-500 italic flex items-center gap-1">
                            <Info className="w-3 h-3" />
                            Clique sur une nouveauté pour la détailler
                        </p>
                    </div>

                    {/* Liste */}
                    <div className="space-y-2">
                        {filtered.length === 0 ? (
                            <p className="text-sm text-slate-400 text-center py-6">Aucune nouveauté dans ce filtre.</p>
                        ) : filtered.map((item, idx) => (
                            <AnnouncementRow
                                key={item.id}
                                item={item}
                                onReset={handleReset}
                                defaultOpen={idx === 0 && filter !== 'seen'}
                            />
                        ))}
                    </div>
                </CardContent>
            )}
        </Card>
    );
}
